package com.shadowings.gero.presentation.viewmodel

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import com.shadowings.gero.domain.model.currentChangelogEntry
import com.shadowings.gero.domain.model.shouldShowChangelog
import com.shadowings.gero.domain.repository.DatabaseRepository
import com.shadowings.gero.domain.repository.UserDataRepository
import com.shadowings.gero.domain.usecase.CheckDailyBonusUseCase
import com.shadowings.gero.domain.usecase.CheckStreakRestorationBackfillUseCase
import com.shadowings.gero.domain.usecase.ScheduleQuizReminderUseCase
import com.shadowings.gero.presentation.model.AppUiState
import com.shadowings.gero.presentation.widget.notifyStrikeWidgetDataChanged
import com.shadowings.gero.util.SoundPlayer
import com.shadowings.gero.util.currentDateAsInt
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.SharingStarted
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.collectLatest
import kotlinx.coroutines.flow.combine
import kotlinx.coroutines.flow.stateIn
import kotlinx.coroutines.launch

class AppViewModel(
    private val userDataRepository: UserDataRepository,
    databaseRepository: DatabaseRepository,
    private val checkDailyBonusUseCase: CheckDailyBonusUseCase,
    private val checkStreakRestorationBackfillUseCase: CheckStreakRestorationBackfillUseCase,
    private val scheduleQuizReminderUseCase: ScheduleQuizReminderUseCase,
) : ViewModel() {

    private val changelogDismissed = MutableStateFlow(false)
    private val dailyBonusDismissed = MutableStateFlow(false)

    private val dialogsState = combine(
        changelogDismissed,
        dailyBonusDismissed
    ) { changelog, bonus -> changelog to bonus }

    val uiState: StateFlow<AppUiState> = combine(
        userDataRepository.userData,
        userDataRepository.themeMode,
        userDataRepository.hapticEnabled,
        databaseRepository.isReady,
        dialogsState,
    ) { userData, themeMode, hapticEnabled, isReady, dialogs ->
        val (dismissedChangelog, dismissedBonus) = dialogs
        val todayDate = currentDateAsInt()
        val showDailyBonus = !dismissedBonus &&
                userData.tutorialDone == true &&
                userData.lastOpenDate > 0 &&
                todayDate > userData.lastOpenDate

        val showChangelog = !dismissedChangelog &&
                !showDailyBonus &&
                shouldShowChangelog(
                    tutorialDone = userData.tutorialDone,
                    lastSeenChangelogVersion = userData.lastSeenChangelogVersion,
                )

        AppUiState(
            tutorialDone = userData.tutorialDone,
            isDatabaseReady = isReady,
            language = userData.language,
            themeMode = themeMode,
            hapticEnabled = hapticEnabled,
            showChangelog = showChangelog,
            showDailyBonus = showDailyBonus,
            userData = userData,
        )
    }.stateIn(viewModelScope, SharingStarted.WhileSubscribed(5000), AppUiState())

    fun dismissChangelog() {
        changelogDismissed.value = true
        viewModelScope.launch(Dispatchers.Default) {
            userDataRepository.setLastSeenChangelogVersion(currentChangelogEntry.version)
        }
    }

    fun claimDailyBonus(isFullVersionUnlocked: Boolean) {
        if (dailyBonusDismissed.value) return
        dailyBonusDismissed.value = true
        viewModelScope.launch(Dispatchers.Default) {
            checkDailyBonusUseCase.claimDailyBonus(
                isFullVersionUnlocked = isFullVersionUnlocked,
                force = false
            )
        }
    }

    fun setFullVersionUnlocked(unlocked: Boolean) {
        viewModelScope.launch(Dispatchers.Default) {
            userDataRepository.setFullVersionUnlocked(unlocked)
        }
    }

    fun completeTutorialForWeb() {
        viewModelScope.launch(Dispatchers.Default) {
            userDataRepository.setTutorialCompleted()
        }
    }


    init {
        viewModelScope.launch(Dispatchers.Default) {
            userDataRepository.soundEnabled.collectLatest { enabled ->
                SoundPlayer.setEnabled(enabled)
            }
        }
        viewModelScope.launch(Dispatchers.Default) {
            checkDailyBonusUseCase.checkStreakReset()
            checkStreakRestorationBackfillUseCase()
            scheduleQuizReminderUseCase()
            notifyStrikeWidgetDataChanged()
        }
    }
}
